/* eslint-disable react/jsx-no-bind */
//@flow
import * as React from 'react';
import classnames from 'classnames';
import _ from 'lodash';

import { Button } from 'common/components/core/button';
import warning from 'common/utils/warning';
import ChartTooltipItem from './ChartTooltipItem';
import { getValueAndUnit } from './ChartHelper';
import { type StacksType, type DataKeyType } from './CartesianChartDataType';
import { type UnitType } from './ChartDataType';

import theme from './GroupedDataTooltip.scss';

type Props = {
    active: boolean,
    label?: string,
    dataKeys: Array<DataKeyType>,
    stacks: StacksType,
    payload: Array<GroupedDataPayloadType>,
    active: boolean,
    unit: UnitType,
    showTotal?: boolean,
    onClose: () => void
};

type GroupedDataPayloadType = {
    color: string,
    dataKey: DataKeyType,
    fill: string,
    formatter: any,
    name: string,
    payload: Object,
    unit: string,
    value: number
};
const GroupedDataTooltip = (props: Props) => {
    const renderItems = (
        dataKeys: Array<DataKeyType>,
        payload: Array<GroupedDataPayloadType>
    ) => {
        const { unit } = props;
        return dataKeys.map(dataKey => {
            const data = payload.find(data => data.dataKey === dataKey.key);
            warning(
                data,
                `The key - ${dataKey.key} is missing in the payload`,
                { metadata: { dataKeys, payload } }
            );
            const { value, valueType } = getValueAndUnit(
                data?.value || 0,
                unit
            );
            return (
                <ChartTooltipItem
                    key={dataKey.key}
                    theme={theme}
                    color={data.color}
                    label={dataKey.label}
                    value={value}
                    valueType={valueType}
                />
            );
        });
    };

    const renderCombinedValue = (dataKeys: Array<DataKeyType>) => {
        const { unit, payload } = props;
        const total = dataKeys.reduce((sum, dataKey) => {
            const data = payload.find(data => data.dataKey === dataKey.key);
            warning(
                data,
                `The key - ${dataKey.key} is missing in the payload`,
                { metadata: { dataKeys, payload } }
            );
            return sum + (data?.value || 0);
        }, 0);
        const { value, valueType } = getValueAndUnit(total, unit);
        return (
            <div className={theme.dataItemValue}>
                {`${value || 0} ${valueType || ''}`}
            </div>
        );
    };

    const { label, stacks, dataKeys, payload, active, showTotal } = props;
    if (!active) {
        return <div />;
    }
    const groupedDataKeys = _.groupBy(dataKeys, 'stackId');
    return (
        <div className={classnames(theme.tooltipWrap, 'draggableCancel')}>
            <div className={theme.tooltipTitle}>
                {label}{' '}
                {showTotal && Object.keys(stacks).length === 1
                    ? renderCombinedValue(dataKeys)
                    : []}
            </div>
            <div className={theme.dataWrap}>
                {Object.keys(groupedDataKeys).map(key => {
                    const dataKeys: Array<DataKey> = groupedDataKeys[key];
                    if (
                        key === 'undefined' ||
                        key === 'null' ||
                        Object.keys(stacks).length === 1
                    ) {
                        return (
                            <div className={theme.dataGroup} key={key}>
                                {renderItems(dataKeys, payload)}
                            </div>
                        );
                    }
                    const stack = stacks[key];
                    return (
                        <div className={theme.dataGroup} key={key}>
                            <span className={theme.dataTitle}>
                                {stack.label}{' '}
                                {showTotal ? renderCombinedValue(dataKeys) : []}
                            </span>
                            <div className={theme.subsection}>
                                {renderItems(dataKeys, payload)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const GroupedDataTooltipWithSelectedState = (props: {
    ...Props,
    forceShow: boolean
}) => {
    const [savedProps, setSavedProps] = React.useState(null);

    React.useEffect(() => {
        if (props.forceShow && !savedProps) {
            setSavedProps(props);
        }
    }, [savedProps, props]);

    const { forceShow, ...tooltipProps } = savedProps || props;

    return (
        <div className={theme.tooltipWrappper}>
            {forceShow ? (
                <Button
                    icon="cancel-thin"
                    ripple={false}
                    className={theme.closeBtn}
                    theme={theme}
                    onClick={e => {
                        setSavedProps(null);
                        tooltipProps.onClose(e);
                    }}
                />
            ) : null}
            <GroupedDataTooltip {...tooltipProps} />
        </div>
    );
};

export { GroupedDataTooltipWithSelectedState };
export default GroupedDataTooltip;
